import React, { Fragment, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { MdClose } from 'react-icons/md';
import isEmpty from 'lodash/isEmpty';
import { AuthContext } from 'context/AuthProvider';
import { AbsenceReportContext } from 'context/AbsenceReportProvider';
import RequestMembershipResult from 'components/UI/RequestMembershipResult/RequestMembershipResult';
import ConnectMembershipResult from 'components/UI/ConnectMembershipResult/ConnectMembershipResult';
import MemberModal from 'components/UI/MemberModal/MemberModal';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import Drawer from 'components/UI/Antd/Drawer/Drawer';
import Progress from 'components/UI/Antd/Progress/Progress';
import Button from 'components/UI/Antd/Button/Button';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import WizardSignUp from './WizardSignUp';
import WizardSignIn from './WizardSignIn';
import WizardRequest from './WizardRequest';
import WizardConnect from './WizardConnect';
import { getStateFromUrl, setStateToUrl } from 'library/helpers/url_handler';
import membership from 'services/membership.js';
import {
  WizardHeader,
  StepHeader,
  StepHeadline,
  ConnectedCardIconWrapper
} from './MembershipsWizard.style.js';
import temCard from 'assets/images/tem-card.png';
import { EmailVerificationContext } from 'context/EmailVerificationProvider';
import useAmplitude from 'library/hooks/useAmplitude';

const AsyncButton = ButtonWithAsync(Button);

// constants
const steps = 2;
export const SIGN_IN_AUTH = 'signin';
export const SIGN_UP_AUTH = 'signup';

export const CONNECT_FLOW = 'connect';
export const REQUEST_FLOW = 'request';

export const GUEST_RESERVATION = 'guest';

const MembershipsWizardTitle = ({ loggedIn, onCloseDrawer }) => {
  const { t } = useTranslation();

  const step = loggedIn ? 2 : 1;
  return (
    <WizardHeader>
      <StepHeader>
        <StepHeadline>{t('STEP_OF_SUBTITLE', { step, steps })}</StepHeadline>
        <Button className='drawer-close-button' onClick={onCloseDrawer}>
          <MdClose size={24} color='#ffffff' />
        </Button>
      </StepHeader>
      <Progress
        strokeLinecap='square'
        strokeColor={{ from: '#194AB9', to: '#194AB9' }}
        percent={Math.floor((100 * step) / steps)}
        showInfo={false}
      />
    </WizardHeader>
  );
};

const MembershipsWizard = ({ location, history, modalClass, showTemModal }) => {
  const { t } = useTranslation();
  const searchParams = getStateFromUrl(location);
  const { bannerVisible } = useContext(EmailVerificationContext);

  const { loggedIn, user, refreshData, selectedBoaterVessel } =
    useContext(AuthContext);
  const { onOpenARBanner } = useContext(AbsenceReportContext);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [flow, setFlow] = useState(REQUEST_FLOW);
  const [auth, setAuth] = useState(SIGN_UP_AUTH);
  const [requestResultOpened, setRequestResultOpened] = useState(false);
  const [connectResultOpened, setConnectResultOpened] = useState(false);
  const [code, setCode] = useState(null);
  const { sendToAmplitude } = useAmplitude();

  useEffect(() => {
    const initMembershipAction = async () => {
      const searchParams = getStateFromUrl(location);
      if (
        loggedIn &&
        !isEmpty(user) &&
        !isEmpty(searchParams) &&
        (searchParams.connect_membership === 'true' ||
          searchParams.request_membership === 'true')
      ) {
        const { vessels } = user;
        const currentVessel = vessels.filter(
          v => v.id === searchParams.vessel_id
        )[0];
        if (
          searchParams.request_membership === 'true' &&
          searchParams.type === 'TEM' &&
          currentVessel &&
          !currentVessel.membership_requests.find(
            req => req.status === 'PENDING'
          )
        ) {
          const { home_marina_id, boat_name, boat_length, type, vessel_id } =
            searchParams;
          const data = {
            home_marina_id,
            boat_name,
            boat_length,
            type,
            vessel_id
          };
          try {
            const res = await membership.request(data);
            if (res && res.error_type) {
              // ToDo: how should we display the error?
              console.error(res);
              return Promise.reject();
            } else {
              setRequestResultOpened(true);
              sendToAmplitude({
                eventProperties: data,
                eventName: 'request_membership'
              });

              return Promise.resolve();
            }
          } catch (err) {
            console.error(err);
            return Promise.reject();
          }
        } else if (
          searchParams.connect_membership === 'true' &&
          searchParams.type === 'TEM' &&
          !code
        ) {
          const { card_number, type } = searchParams;
          const data = {
            card_number,
            type
          };
          try {
            const res = await membership.connect(data);
            if (res && res.error_type) {
              // ToDo: how should we display the error?
              console.error(res);
              return Promise.reject();
            } else {
              await refreshData({ parameters: ['vessels'] });
              setCode(card_number);
              setConnectResultOpened(true);
              sendToAmplitude({
                eventProperties: data,
                eventName: 'connect_membership'
              });
              return Promise.resolve();
            }
          } catch (err) {
            console.error(err);
            return Promise.reject();
          }
        }
      }
    };
    initMembershipAction();
  }, [loggedIn, location]);

  const onCloseRequestResult = async () => {
    setRequestResultOpened(false);
    await refreshData({
      parameters: ['vessels'],
      vessel_id: selectedBoaterVessel
    });
    // clear search params after connect
    setTimeout(() => {
      history.replace({
        pathname: location.pathname,
        search: setStateToUrl({ request_membership: true })
      });
    }, 0);
    onOpenARBanner();
  };

  const onCloseConnectResult = async () => {
    setConnectResultOpened(false);
    setCode(null);
    onOpenARBanner();
    // clear search params after connect
    setTimeout(() => {
      history.replace({
        pathname: location.pathname,
        search: setStateToUrl({ connect_membership: true })
      });
    }, 0);
  };

  const onOpenDrawer = flow => {
    setFlow(flow);
    setDrawerVisible(true);
  };

  const onCloseDrawer = () => {
    setDrawerVisible(false);
  };

  return (
    <Fragment>
      <MemberModal
        onOpenRequestDrawer={() => onOpenDrawer(REQUEST_FLOW)}
        onOpenConnectDrawer={() => onOpenDrawer(CONNECT_FLOW)}
        isReservationFlowContinue={
          loggedIn &&
          !isEmpty(user) &&
          !isEmpty(searchParams) &&
          searchParams.is_reservation_creation === 'true'
        }
        isRequestFlowContinue={
          loggedIn &&
          !isEmpty(user) &&
          !isEmpty(searchParams) &&
          searchParams.request_membership === 'true'
        }
        isConnectFlowContinue={
          loggedIn &&
          !isEmpty(user) &&
          !isEmpty(searchParams) &&
          searchParams.connect_membership === 'true'
        }
        showTemModal={ showTemModal }
        className={bannerVisible ? 'moved-down' : ''}
      />
      <Drawer
        className='wizard-drawer'
        placement='right'
        title={
          <MembershipsWizardTitle
            loggedIn={loggedIn && user}
            onCloseDrawer={onCloseDrawer}
          />
        }
        placement='right'
        closable={false}
        onClose={onCloseDrawer}
        visible={drawerVisible}
        maskStyle={{ backgroundColor: 'transparent' }}
        width={426}
        zIndex={10001}
      >
        {loggedIn && user ? (
          <Fragment>
            {flow === REQUEST_FLOW && (
              <WizardRequest
                onConnectFlow={() => setFlow(CONNECT_FLOW)}
                onCloseDrawer={onCloseDrawer}
              />
            )}
            {flow === CONNECT_FLOW && (
              <WizardConnect
                onRequestFlow={() => setFlow(REQUEST_FLOW)}
                onCloseDrawer={onCloseDrawer}
              />
            )}
          </Fragment>
        ) : (
          <Fragment>
            {auth === SIGN_UP_AUTH && (
              <WizardSignUp
                flow={flow}
                onSignInAuth={() => setAuth(SIGN_IN_AUTH)}
              />
            )}
            {auth === SIGN_IN_AUTH && (
              <WizardSignIn
                flow={flow}
                onSignUpAuth={() => setAuth(SIGN_UP_AUTH)}
              />
            )}
          </Fragment>
        )}
      </Drawer>
      <RequestMembershipResult
        visible={requestResultOpened}
        title={t('REQUEST_WIZARD_RESULT_TITLE')}
        onCancel={onCloseRequestResult}
        subtitle={
          <Fragment>
            <p>{t('REQUEST_WIZARD_RESULT_SUBTITLE')}</p>
          </Fragment>
        }
        actions={
          <AsyncButton
            asyncAction={onCloseRequestResult}
            htmlType='button'
            type='primary'
            size='large'
            label={t('OK_CAPITALIZED')}
          />
        }
      />
      <ConnectMembershipResult
        visible={connectResultOpened}
        title={t('CONNECT_WIZARD_RESULT_TITLE')}
        onCancel={onCloseConnectResult}
        subtitle={
          <Fragment>
            <p>
              {t('CONNECT_WIZARD_RESULT_SUBTITLE')}
              {code && <b>{code}</b>}
            </p>
          </Fragment>
        }
        actions={
          <AsyncButton
            asyncAction={onCloseConnectResult}
            htmlType='button'
            type='primary'
            size='large'
            label={t('OK_CAPITALIZED')}
          />
        }
      >
        <ConnectedCardIconWrapper>
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: '33px' }} />
          <img alt={'Connect temp card'} src={temCard} />
        </ConnectedCardIconWrapper>
      </ConnectMembershipResult>
    </Fragment>
  );
};

export default withRouter(MembershipsWizard);
